import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import { CircularProgress, Link, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { EligibilityTaskLookup } from '../../interfaces/eligibility';
import { PropagationOutcome } from '../../interfaces/referral';
import eligibilityService from '../../services/eligibility';
import { getEligibilityTaskUrl } from '../../utils/eligibilityTask.utils';

export const NO_TASK_COPY = 'No eligibility task yet';
export const ELIGIBILITY_FAILED_COPY = 'Eligibility check failed — see Propagation status';
export const LOOKUP_FAILED_COPY = "Couldn't load the eligibility task. Refresh to try again.";
export const LOOKUP_UNAVAILABLE_COPY = 'Eligibility task lookup is unavailable.';

interface EligibilityTaskLinkProps {
  /** The referral's CRM lead id (LeadSquared id), which eligibility keys its task on. */
  crmId?: string | null;
  /**
   * The referral's last delivery to eligibility, from its propagation ledger. When it failed
   * and no task exists, that failure is why, and the Propagation status panel says more.
   */
  eligibilityPropagation?: PropagationOutcome;
}

/**
 * From a referral to its eligibility task. Both systems share the CRM lead id, so the task is
 * found by it: eligibility gets a link to the referral, never a copy of its data.
 *
 * Used on the referral card and, once that page lands, on the Referrals hub detail page.
 */
const EligibilityTaskLink: React.FC<EligibilityTaskLinkProps> = ({
  crmId,
  eligibilityPropagation,
}) => {
  const [lookup, setLookup] = useState<EligibilityTaskLookup | null>(null);

  useEffect(() => {
    if (!crmId) {
      return;
    }

    let active = true;
    setLookup(null);
    eligibilityService.findTaskByCrmId(crmId).then((result) => {
      if (active) {
        setLookup(result);
      }
    });

    return () => {
      active = false;
    };
  }, [crmId]);

  // Without a CRM lead there is nothing for eligibility to have keyed a task on, and the card
  // already says the lead is missing. A caller without eligibility access sees nothing either.
  if (!crmId || lookup?.status === 'forbidden') {
    return null;
  }

  if (!lookup) {
    return (
      <Typography
        variant='caption'
        color='text.secondary'
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <CircularProgress size={10} /> Looking up the eligibility task…
      </Typography>
    );
  }

  if (lookup.status === 'found') {
    return (
      <Link
        component={RouterLink}
        to={getEligibilityTaskUrl(lookup.taskId)}
        variant='caption'
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none' }}>
        <AssignmentTurnedInIcon sx={{ fontSize: 12 }} />
        Eligibility task #{lookup.taskId}
      </Link>
    );
  }

  // The lookup itself failing says nothing about the referral's delivery, so it gets its own
  // copy rather than pointing at the Propagation status panel.
  if (lookup.status === 'error') {
    return (
      <Typography variant='caption' color='text.secondary' display='block'>
        {LOOKUP_FAILED_COPY}
      </Typography>
    );
  }

  // eligibility-api ignored the crmId filter (it predates it), so it could not say whether the
  // lead has a task. "No eligibility task yet" would be a guess, and often a wrong one.
  if (lookup.status === 'unavailable') {
    return (
      <Typography variant='caption' color='text.secondary' display='block'>
        {LOOKUP_UNAVAILABLE_COPY}
      </Typography>
    );
  }

  const deliveryFailed = eligibilityPropagation?.status === 'failed';

  return (
    <Typography
      variant='caption'
      color={deliveryFailed ? 'error' : 'text.secondary'}
      display='block'>
      {deliveryFailed ? ELIGIBILITY_FAILED_COPY : NO_TASK_COPY}
    </Typography>
  );
};

export default EligibilityTaskLink;
